<script setup lang="ts">
import { applyTheme, theme, VALID_THEMES } from '@/event/theme/theme'
import { applyCardTheme, cardTheme, VALID_THEMES_CARD } from '@/event/theme/card'
import { ref } from 'vue'

const STORAGE_KEY = 'dev-theme-switcher-collapsed'
const collapsed = ref<boolean>(localStorage.getItem(STORAGE_KEY) === '1')

function toggleCollapsed() {
    collapsed.value = !collapsed.value
    localStorage.setItem(STORAGE_KEY, collapsed.value ? '1' : '0')
}

function next() {
    const idx = VALID_THEMES.indexOf(theme.value)
    applyTheme(VALID_THEMES[(idx + 1) % VALID_THEMES.length])
}

function prev() {
    const idx = VALID_THEMES.indexOf(theme.value)
    applyTheme(VALID_THEMES[(idx - 1 + VALID_THEMES.length) % VALID_THEMES.length])
}

const cardOptions = Object.values(VALID_THEMES_CARD).filter((v, i, a) => a.indexOf(v) === i)
</script>

<template>
    <!-- Collapsed: tiny floating button -->
    <button
        v-if="collapsed"
        class="dev-theme-fab"
        @click="toggleCollapsed"
        title="Dev Theme Switcher"
    >🎨</button>

    <!-- Expanded panel -->
    <div v-else class="dev-theme-switcher">
        <div class="dev-theme-header">
            <div class="dev-theme-current">🎨 {{ theme }}</div>
            <button class="dev-theme-close" @click="toggleCollapsed" title="Gizle">✕</button>
        </div>
        <div class="dev-theme-buttons">
            <button @click="prev">◀</button>
            <button @click="next">▶</button>
        </div>
        <div class="dev-theme-list">
            <button
                v-for="t in VALID_THEMES"
                :key="t"
                :class="{ active: t === theme }"
                @click="applyTheme(t)"
            >{{ t }}</button>
        </div>
        <div class="dev-card-label">🃏 card: {{ cardTheme }}</div>
        <div class="dev-card-list">
            <button
                v-for="c in cardOptions"
                :key="c"
                :class="{ active: c === cardTheme }"
                @click="applyCardTheme(c)"
            >{{ c }}</button>
        </div>
    </div>
</template>

<style scoped>
.dev-theme-switcher {
    position: fixed;
    bottom: 16px;
    right: 16px;
    z-index: 99999;
    background: #1a1a1a;
    color: #fff;
    border-radius: 10px;
    padding: 10px;
    font-size: 12px;
    font-family: monospace;
    box-shadow: 0 4px 20px rgba(0,0,0,0.5);
    min-width: 180px;
}
.dev-theme-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 6px;
}
.dev-theme-current {
    font-weight: bold;
    color: #f0c040;
}
.dev-theme-close {
    background: #333;
    color: #fff;
    border: none;
    border-radius: 4px;
    width: 22px;
    height: 22px;
    font-size: 12px;
    line-height: 1;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
}
.dev-theme-close:hover {
    background: #555;
}
.dev-theme-fab {
    position: fixed;
    bottom: 16px;
    right: 16px;
    z-index: 99999;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #1a1a1a;
    color: #fff;
    border: 1px solid #444;
    font-size: 18px;
    cursor: pointer;
    box-shadow: 0 4px 20px rgba(0,0,0,0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    transition: transform 0.15s ease, background 0.15s ease;
}
.dev-theme-fab:hover {
    background: #2a2a2a;
    transform: scale(1.08);
}
.dev-theme-buttons {
    display: flex;
    gap: 6px;
    margin-bottom: 8px;
}
.dev-theme-buttons button {
    flex: 1;
    background: #333;
    color: #fff;
    border: none;
    border-radius: 4px;
    padding: 4px;
    cursor: pointer;
}
.dev-theme-buttons button:hover {
    background: #555;
}
.dev-theme-list {
    display: flex;
    flex-direction: column;
    gap: 3px;
    max-height: 300px;
    overflow-y: auto;
}
.dev-theme-list button {
    background: #2a2a2a;
    color: #ccc;
    border: 1px solid #444;
    border-radius: 4px;
    padding: 3px 8px;
    cursor: pointer;
    text-align: left;
}
.dev-theme-list button:hover {
    background: #3a3a3a;
    color: #fff;
}
.dev-theme-list button.active {
    background: #f0c040;
    color: #000;
    border-color: #f0c040;
    font-weight: bold;
}
.dev-card-label {
    font-weight: bold;
    margin-top: 10px;
    margin-bottom: 4px;
    color: #80cbc4;
}
.dev-card-list {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}
.dev-card-list button {
    background: #2a2a2a;
    color: #ccc;
    border: 1px solid #444;
    border-radius: 4px;
    padding: 3px 8px;
    cursor: pointer;
    font-size: 11px;
}
.dev-card-list button:hover {
    background: #3a3a3a;
    color: #fff;
}
.dev-card-list button.active {
    background: #80cbc4;
    color: #000;
    border-color: #80cbc4;
    font-weight: bold;
}
</style>
